import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  alpha,
  Card,
  useTheme,
} from '@mui/material';
import {
  Analytics,
  Cancel,
  CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

function BatchResultsSection({ batchResults }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const total = batchResults.length;
  const successCount = batchResults.filter((result) => result.success).length;
  const failedCount = total - successCount;
  const totalDataPoints = batchResults.reduce((sum, result) => sum + (result.data_points || 0), 0);
  const totalAlerts = batchResults.reduce((sum, result) => sum + (result.alerts_generated || 0), 0);

  const cardSx = {
    mb: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: 1,
    overflow: 'hidden',
  };

  const headerSx = {
    p: 3,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    bgcolor: alpha(theme.palette.primary.main, 0.02),
  };

  const heroIconSx = {
    width: 48,
    height: 48,
    borderRadius: 1,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  };

  const titleSx = { letterSpacing: -0.5, mb: 0.5 };

  const statLabelSx = {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  const tableHeadRowSx = {
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    '& th': {
      fontWeight: 700,
      fontSize: '0.875rem',
      borderBottom: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
      py: 2,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: 'text.secondary',
    },
  };

  const rowSx = {
    '&:hover': {
      bgcolor: alpha(theme.palette.action.hover, 0.3),
    },
    '& td': {
      py: 1.5,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
  };

  const successChipSx = {
    fontWeight: 600,
    height: 28,
    bgcolor: theme.palette.success.main,
    color: 'white',
    '& .MuiChip-icon': {
      fontSize: 16,
      color: 'white',
    },
    '& .MuiChip-label': {
      color: 'white',
    },
  };

  const errorChipSx = {
    fontWeight: 600,
    height: 28,
    '& .MuiChip-icon': {
      fontSize: 16,
    },
  };

  const monoTextSx = { fontFamily: 'monospace' };

  return (
    <Card elevation={0} sx={cardSx}>
      <Box sx={headerSx}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box sx={heroIconSx}>
            <Analytics sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={titleSx}>
              {t('downtrend.batchResults.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {total} {t('downtrend.batchResults.totalSymbols', 'mã cổ phiếu đã phân tích')}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={statLabelSx}>
              {t('downtrend.batchResults.success', 'Thành công')}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mt: 0.5 }}>
              {successCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={statLabelSx}>
              {t('downtrend.batchResults.failed', 'Thất bại')}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.error.main, mt: 0.5 }}>
              {failedCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={statLabelSx}>
              {t('downtrend.batchResults.dataPoints', 'Điểm dữ liệu')}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.primary.main, mt: 0.5 }}>
              {totalDataPoints.toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={statLabelSx}>
              {t('downtrend.batchResults.alertsGenerated', 'Cảnh báo')}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.warning.main, mt: 0.5 }}>
              {totalAlerts.toLocaleString()}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadRowSx}>
              <TableCell>{t('downtrend.batchResults.symbol')}</TableCell>
              <TableCell>{t('downtrend.batchResults.status')}</TableCell>
              <TableCell align="right">{t('downtrend.batchResults.dataPoints')}</TableCell>
              <TableCell align="right">{t('downtrend.batchResults.alertsGenerated')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batchResults.map((result) => (
              <TableRow
                key={`${result.symbol}-${result.success ? 'success' : result.error || 'failed'}`}
                hover
                sx={rowSx}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={700} sx={monoTextSx}>
                    {result.symbol}
                  </Typography>
                </TableCell>
                <TableCell>
                  {result.success ? (
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 16, color: 'white' }} />}
                      label={t('downtrend.batchResults.success')}
                      color="success"
                      size="small"
                      sx={successChipSx}
                    />
                  ) : (
                    <Chip
                      icon={<Cancel sx={{ fontSize: 16 }} />}
                      label={result.error || t('downtrend.batchResults.failed')}
                      color="error"
                      size="small"
                      sx={errorChipSx}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} sx={monoTextSx}>
                    {(result.data_points || 0).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} sx={monoTextSx}>
                    {(result.alerts_generated || 0).toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

BatchResultsSection.propTypes = {
  batchResults: PropTypes.arrayOf(PropTypes.shape({
    symbol: PropTypes.string,
    success: PropTypes.bool,
    error: PropTypes.string,
    data_points: PropTypes.number,
    alerts_generated: PropTypes.number,
  })).isRequired,
};

export default BatchResultsSection;
